/**
 * Polygon Details Modal
 *
 * Shows comprehensive information about a polygon including:
 * - Basic metadata (name, producer, commodity)
 * - Geographic information (area, coordinates, CRS)
 * - GeoJSON geometry
 * - Timestamps
 */

import { X, MapPin, Calendar, Globe, Layers, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

interface PolygonDetailsModalProps {
    polygon: any;
    onClose: () => void;
}

export default function PolygonDetailsModal({ polygon, onClose }: PolygonDetailsModalProps) {
    const [copied, setCopied] = useState(false);
    const [geoExpanded, setGeoExpanded] = useState(false);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
            <div className="card p-0 w-full max-w-4xl max-h-[90vh] overflow-hidden animate-slide-up flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 flex items-center justify-center">
                            <MapPin className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Detalles del Polígono
                            </h2>
                            <p className="text-sm text-gray-500">{polygon.name}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Basic Information */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Información Básica
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">ID</p>
                                <p className="text-sm font-mono text-gray-900 dark:text-white">{polygon.id}</p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Nombre</p>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{polygon.name}</p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Productor</p>
                                <p className="text-sm text-gray-900 dark:text-white">
                                    {polygon.producer_name || 'No especificado'}
                                </p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Nombre Legal</p>
                                <p className="text-sm text-gray-900 dark:text-white">
                                    {polygon.legal_name || 'No especificado'}
                                </p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Organización</p>
                                <p className="text-sm text-gray-900 dark:text-white">
                                    {polygon.organization || 'No especificado'}
                                </p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Commodity</p>
                                <p className="text-sm text-gray-900 dark:text-white capitalize">{polygon.commodity}</p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">País</p>
                                <p className="text-sm text-gray-900 dark:text-white">
                                    {polygon.country || 'No especificado'}
                                </p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Región</p>
                                <p className="text-sm text-gray-900 dark:text-white">
                                    {polygon.region || 'No especificado'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Geographic Information */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <Globe className="w-5 h-5" />
                            Información Geográfica
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Área</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {polygon.area_ha?.toFixed(2) || 'N/A'} ha
                                </p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Latitud</p>
                                <p className="text-lg font-mono text-gray-900 dark:text-white">
                                    {polygon.centroid_lat?.toFixed(6) || 'N/A'}°
                                </p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Longitud</p>
                                <p className="text-lg font-mono text-gray-900 dark:text-white">
                                    {polygon.centroid_lon?.toFixed(6) || 'N/A'}°
                                </p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Sistema de Coordenadas</p>
                                <p className="text-sm font-mono text-gray-900 dark:text-white">
                                    {polygon.original_crs || 'EPSG:4326'}
                                </p>
                            </div>
                            {polygon.source_format && (
                                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                    <p className="text-xs text-gray-500 mb-1">Formato Original</p>
                                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                        {polygon.source_format}
                                    </span>
                                </div>
                            )}
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Estado</p>
                                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                    polygon.status === 'active'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                }`}>
                                    {polygon.status}
                                </span>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Cumplimiento EUDR</p>
                                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                    polygon.compliance_status === 'compliant'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                        : polygon.compliance_status === 'non_compliant'
                                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                }`}>
                                    {polygon.compliance_status || 'pending'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Timestamps */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            Fechas
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Creado</p>
                                <p className="text-sm text-gray-900 dark:text-white">
                                    {formatDate(polygon.created_at)}
                                </p>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Última Actualización</p>
                                <p className="text-sm text-gray-900 dark:text-white">
                                    {formatDate(polygon.updated_at)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* GeoJSON Geometry */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <button
                                onClick={() => setGeoExpanded(!geoExpanded)}
                                className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                            >
                                <Layers className="w-5 h-5" />
                                Geometría GeoJSON
                                {geoExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            <button
                                onClick={() => copyToClipboard(JSON.stringify(polygon.geometry, null, 2))}
                                className="btn-secondary text-sm flex items-center gap-2"
                            >
                                {copied ? (
                                    <>
                                        <Check className="w-4 h-4" />
                                        Copiado
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4" />
                                        Copiar
                                    </>
                                )}
                            </button>
                        </div>
                        {!geoExpanded ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                                {(() => {
                                    const geom = polygon.geometry;
                                    const g = geom?.type === 'Feature' ? geom.geometry : geom;
                                    const type = g?.type || 'Unknown';
                                    const coords = g?.coordinates;
                                    const count = type === 'Polygon'
                                        ? coords?.[0]?.length || 0
                                        : type === 'MultiPolygon'
                                        ? coords?.reduce((sum: number, poly: any) => sum + (poly?.[0]?.length || 0), 0) || 0
                                        : 0;
                                    return `${type} · ${count} puntos`;
                                })()}
                            </p>
                        ) : (
                            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-h-[300px] overflow-y-auto">
                                <pre className="text-xs text-green-400 font-mono">
                                    {JSON.stringify(polygon.geometry, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>

                    {/* Metadata (if exists) */}
                    {polygon.metadata && Object.keys(polygon.metadata).length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Metadatos Adicionales
                            </h3>
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                <pre className="text-xs text-gray-700 dark:text-gray-300 font-mono overflow-x-auto">
                                    {JSON.stringify(polygon.metadata, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={onClose} className="btn-secondary">
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
}
